<template>
    <PlanbordGridCell
        :weekend="isWeekend"
        :hasWish="hasWish"
        :hasLeaveProblems="!isInStampPeriod && hasLeaveProblems"
        :holiday="day.holiday"
        :isStampPeriodStart="isStampPeriodStart"
        :isInStampPeriod="isInStampPeriod"
        :hasStamp="hasStamp"
        :isIterationStart="isIterationStart"
        :isIterationEnd="isIterationEnd"
        :isStampStart="isStampStart"
        :isStampEnd="isStampEnd"
        :isPublishedEndDate="isPublishedEndDate"
        :isProposedEndDate="isProposedEndDate"
        :isProposed="isProposed"
        :isWishInputEndDate="wishInputEndDate"
        :isNotEmployed="!isEmployed"
        :hasLeavePeriodsOrForm="hasLeavePeriodsOrForm"
        :rosterEnded="day.ended"
        :contractDeviations="contractDeviations"
        :shiftStateDeviations="usedShiftDeviationStates"
        :sicknessInformation="sicknessInformation"
        :unavailabilityStatus="unavailabilityStatus"
        :data-x="dayIndex"
        @mousedown.left="handleClickEvents"
        @mousemove="addCellToMultiSelectRange"
        @contextmenu.stop="showMenu"
        @touchstart.passive="startTouch"
        @touchmove.passive="mobileContext.moveTouch"
        @touchend.passive="mobileContext.endTouch"
    >
        <div class="weekend-identifier" v-if="weekendIdentifier !== null">
            {{ weekendIdentifier }}
        </div>
        <!-- Collection of shifts -->
        <StampCell
            v-if="isInStampPeriod"
            :stampShifts="stampShifts"
            :day="day"
            :dayIndex="dayIndex"
            :resource="resource"
            :stamp="stamp"
            :stampDayIndex="stamp ? stampDayIndex : undefined"
            :resourceIndex="resourceIndex"
            :isAddButton="true"
            :isEmployed="isEmployed"
            :rosterId="roster.rosterId"
            :rosterNames="rosterNames"
            :shifts="shifts"
            :editable="editable"
            :fromOtherRoster="!isOnThisRoster"
            :wish="wish"
            :unavailabilities="unavailabilities"
            @create:stampShift="$emit('create:stampShift', $event)"
        />
        <DeploymentCell
            v-else
            :company="company"
            :companyEmployee="companyEmployee"
            :day="day"
            :dayIndex="dayIndex"
            :deployments="deployments"
            :deploymentProposals="deploymentProposals"
            :isResourceOfThisRoster="isOnThisRoster"
            :resource="resource"
            :resourceIndex="resourceIndex"
            :rosterNames="rosterNames"
            :isEmployed="isEmployed"
            :showMarkerWarnings="shouldShowMarkerWarnings"
            :rosterId="roster.rosterId"
            :organisationDeploymentStates="organisationDeploymentStates"
            :shifts="shifts"
            :editable="editable"
            :canProposeDeployments="canProposeDeployments"
            :wish="wish"
            :unavailabilities="unavailabilities"
            @create:deployment="$emit('create:deployment', $event)"
            @update:deployment="$emit('update:deployment', $event)"
            @delete:deployment="$emit('delete:deployment', $event)"
        />
        <div class="deviation-labels" v-if="!!contractDeviations || (hasLeavePeriodsOrForm && !usedShiftDeviationStates)">
            <span
                v-for="key in contractDeviations"
                :key="key"
                class="deviation-marker"
            >
                {{ contractDeviationLabels![key] }}
            </span>
            <span
                v-if="hasLeavePeriodsOrForm && !usedShiftDeviationStates"
                class="deviation-marker"
            >
                {{ freeDeploymentStateCaption }}
            </span>
        </div>
        <div
            v-if="hasComment"
            class="comment-content"
            @click.stop
            @mousedown.stop
            @mouseup.stop
            @click="openCommentEditor"
        >
            {{ resource!.employment!.commentsByDate![day.formattedForComputers]!.content }}
        </div>
    </PlanbordGridCell>
</template>

<script lang="ts">
    import DeploymentCell from '@/components/Planbord/DeploymentCell.vue';
    import StampCell from '@/components/Planbord/StampCell.vue';
    import EmploymentCommentModal from '@/components/modals/EmploymentCommentModal.vue';
    import DeploymentCellContextMenu from '@/components/Planbord/DeploymentCellContextMenu.vue';
    import {
        ContractedTime,
        OrganisationDeploymentState,
        Deployment,
        LeavePeriod,
        PersonalStampShift,
        ContractCaptions,
        AssignedDay,
        SickPeriod,
        PersonalPlanningWish,
        PersonalStamp,
        Shift,
        Unavailability,
        ResourceRosterAssignment,
        DeploymentProposal,
        DeploymentStateNumber,
        RosterDayType,
        DeploymentState,
    } from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {PlanbordLeavePeriod, ResourceWithKeyedByDateObjects, PlanbordResource, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments} from '@/types/api-object-extensions';
    import AvailabilityUtil from '@/util/AvailabilityUtil';
    import {RosterDay, RosterData, ScreenCoordinates, PermissionsForRoster} from '@/types/types';
    import useMobileContextMenu from '@/util/useMobileContextMenu';
    import {ContextMenuData, MobileContextMenu, SicknessInformation, UnavailabilityStatus} from '@/components/types';
    import Planbord from '@/components/Planbord.vue';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import PlanbordGrid from '@/components/Planbord/PlanbordGrid.vue';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import CellCapacity from '@/util/CellCapacity';

    @Component({
        components: {
            DeploymentCell,
            StampCell,
            PlanbordGridCell,
        },
        emits: [
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:stampShift',
        ],
    })
    export default class PlanbordCell extends Vue {
        @Prop({required: true}) public day!: RosterDay;
        @Prop() public resource?: ResourceWithKeyedByDateObjects | PlanbordResource;
        @Prop() public company?: CompanyWithDeploymentsAndEmployees;
        @Prop() public companyEmployee?: CompanyEmployeeWithDeployments;
        @Prop() public stamp?: PersonalStamp;
        @Prop({type: Number, required: true}) public dayIndex!: number;
        @Prop({type: Number, required: true}) public resourceIndex!: number;
        @Prop() public wish?: PersonalPlanningWish;
        @Prop({required: true}) public leavePeriods!: Array<PlanbordLeavePeriod | LeavePeriod>;
        @Prop({type: Boolean}) public hasLeaveForm!: boolean;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop() public contractDeviationLabels?: ContractCaptions;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public permissions!: Record<number, PermissionsForRoster>;

        private mobileContext: MobileContextMenu = useMobileContextMenu();

        public get isInStampPeriod(): boolean {
            return this.resource !== undefined && (this.resource.stampDeployedUntil === null || this.day.date > this.resource.stampDeployedUntil);
        }

        public get allowedToViewContextMenu(): boolean {
            if (this.isOnThisRoster) {
                return this.editable
                    || this.permissionsForThisCell[UserPermissions.EditLeavePeriods]
                    || this.permissionsForThisCell[UserPermissions.EditSicknessCases]
                    || this.permissionsForThisCell[UserPermissions.EditEmploymentComment]
                    || this.permissionsForThisCell[UserPermissions.EditWishes];
            }

            if (this.hasRosterAssignmentOnThisRoster) {
                return this.canProposeDeployments;
            }

            return false;
        }

        public startTouch(event: TouchEvent): void {
            if (!(event.target as HTMLElement).draggable) {
                this.setActiveCell();
            }
            const promise: Promise<ScreenCoordinates> | null = this.mobileContext.startTouch(event);
            if (promise) {
                promise.then(this.showContextMenu).catch((): void => {
                    return;
                });
            }
        }

        public showContextMenu(coordinates: ScreenCoordinates): void {
            if (!this.allowedToViewContextMenu) {
                return;
            }

            this.setActiveCellWithoutAutocomplete();
            if (this.isOnThisRoster || this.canProposeDeployments) {
                const data: ContextMenuData = {
                    day: this.day,
                    resource: this.resource,
                    company: this.company,
                    companyEmployee: this.companyEmployee,
                    leavePeriods: this.leavePeriods,
                    stamp: this.stamp,
                    hasStamp: this.hasStamp,
                    stampStart: this.isStampStart,
                    stampEnd: this.isStampEnd,
                    sickPeriods: this.sickPeriods,
                    /** @see PlanbordGrid.getSelectedCells() */
                    // @ts-expect-error TS2339
                    isMultiSelect: PlanbordGrid.__vfdConstructor.getSelectedCells().length > 1,
                    /** @see PlanbordGrid.getSelectedCells() */
                    /** @see PlanbordGrid.getPlanbordCellsFromCoordinates() */
                    // @ts-expect-error TS2339
                    multiSelectTargets: PlanbordGrid.__vfdConstructor.getPlanbordCellsFromCoordinates(PlanbordGrid.__vfdConstructor.getSelectedCells()),
                    isOnCurrentRoster: this.isOnThisRoster,
                };
                /** @see DeploymentCellContextMenu.showMenu() */
                // @ts-expect-error TS2339
                DeploymentCellContextMenu.__vfdConstructor.showMenu(coordinates.x, coordinates.y, data);
                /** @see KeyboardDeploymentAutocomplete.lastKeyIsNotControl() */
                // @ts-expect-error TS2339
                KeyboardDeploymentAutocomplete.__vfdConstructor.lastKeyIsNotControl();
            }
        }

        public handleClickEvents(event: MouseEvent): void {
            if ((event.target as HTMLElement).draggable) {
                return;
            }
            this.setActiveCell();
            this.startMultiselectRange();
        }

        public setActiveCell(): void {
            if (this.resourceIndex === undefined) {
                return;
            }

            /** @see PlanbordGrid.isCellAlreadySelected() */
            // @ts-expect-error TS2339
            if (!PlanbordGrid.__vfdConstructor.isCellAlreadySelected({x: this.dayIndex, y: this.resourceIndex})) {
                /** @see PlanbordGrid.resetGrid() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.resetGrid();
            }
            /** @see Planbord.setActiveCell() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.setActiveCell(this.dayIndex, this.resourceIndex, false);
        }

        public setActiveCellWithoutAutocomplete(): void {
            if (this.resourceIndex === undefined) {
                return;
            }

            /** @see PlanbordGrid.isCellAlreadySelected() */
            // @ts-expect-error TS2339
            if (!PlanbordGrid.__vfdConstructor.isCellAlreadySelected({x: this.dayIndex, y: this.resourceIndex})) {
                /** @see PlanbordGrid.resetGrid() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.resetGrid();
            }
            /** @see PlanbordGrid.setActiveCell() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.setActiveCell({
                x: this.dayIndex,
                y: Number(this.resourceIndex),
            });
        }

        public startMultiselectRange(): void {
            if (this.resourceIndex === undefined) {
                return;
            }
            /** @see PlanbordGrid.activateMouseMultiSelect() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.activateMouseMultiSelect({
                x: this.dayIndex,
                y: Number(this.resourceIndex),
            });
        }

        public addCellToMultiSelectRange(): void {
            /** @see PlanbordGrid.getIsMouseMultiSelectActive() */
            // @ts-expect-error TS2339
            if (!PlanbordGrid.__vfdConstructor.getIsMouseMultiSelectActive()) {
                return;
            }
            /** @see Planbord.setActiveCell() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.setActiveCell(this.dayIndex, this.resourceIndex!, false);
            /** @see PlanbordGrid.selectCell() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.selectCell({
                x: this.dayIndex,
                y: this.resourceIndex!,
            });
        }

        public showMenu(e: MouseEvent): void {
            if (this.mobileContext.isTargetedDevice.value) {
                return;
            }
            e.preventDefault();
            this.showContextMenu({x: e.clientX, y: e.clientY});
        }

        public get isSalaryAccepted(): boolean {
            return this.resource !== undefined && this.resource.salaryAcceptedUntil > this.day.date;
        }

        public get editable(): boolean {
            if (this.isSalaryAccepted) {
                return false;
            }

            if (!this.isOnThisRoster) {
                return false;
            }

            if (this.company?.openShiftMarketplaceSyncEnabled && !this.companyEmployee) {
                // allow editing existing item, prevent adding anything.
                return this.deployments.length > 0 || this.deploymentProposals.length > 0;
            }

            if (this.day.proposed) {
                return this.permissionsForThisCell[UserPermissions.EditPublishedPeriod];
            }

            if (this.isInStampPeriod) {
                return this.permissionsForThisCell[UserPermissions.EditStampPeriod];
            }

            return this.permissionsForThisCell[UserPermissions.EditConstructionPeriod];
        }

        public get canProposeDeployments(): boolean {
            if (!this.hasRosterAssignmentOnThisRoster) {
                return false;
            }

            if (this.isSalaryAccepted) {
                return false;
            }

            if (this.isInStampPeriod) {
                return false;
            }

            if (this.contractedTime === null) {
                return false;
            }

            if (!this.isEmployed) {
                return false;
            }

            if (this.day.proposed) {
                return this.permissions[this.contractedTime.rosterId][UserPermissions.ProposeDeploymentsPublishedPeriod];
            }

            return this.permissions[this.contractedTime.rosterId][UserPermissions.ProposeDeploymentsConstructionPeriod];
        }

        public get permissionsForThisCell(): PermissionsForRoster {
            return this.permissions[this.contractedTime?.rosterId ?? this.roster.rosterId];
        }

        public get isOnThisRoster(): boolean {
            return !!this.company
                || !!this.companyEmployee
                || (!!this.contractedTime && this.contractedTime.rosterId === this.roster.rosterId);
        }

        public get contractedTime(): ContractedTime | null {
            if (!this.resource) {
                return null;
            }

            return this.resource.contractedTimes!.find((contractedTime: ContractedTime): boolean => contractedTime.beginDate <= this.day.date && contractedTime.endDate >= this.day.date) ?? null;
        }

        public get hasRosterAssignmentOnThisRoster(): boolean {
            if (!this.resource) {
                return false;
            }

            return this.resource.rosterAssignments?.some((rosterAssignment: ResourceRosterAssignment): boolean =>
                rosterAssignment.rosterId === this.roster.rosterId
                && rosterAssignment.beginDate <= this.day.date
                && (rosterAssignment.endDate === null || rosterAssignment.endDate >= this.day.date
                )) ?? false;
        }

        public get freeDeploymentStateCaption(): string {
            const deploymentStates: Record<number, OrganisationDeploymentState> = this.organisationDeploymentStates;
            if (DeploymentStateNumber.Free in deploymentStates) {
                return deploymentStates[DeploymentStateNumber.Free].caption;
            }

            return 'Verlof';
        }

        public get deployments(): Deployment[] {
            if (this.resource) {
                return this.resource.deployments[this.day.formattedDateIso] || [];
            }

            if (this.companyEmployee) {
                return this.companyEmployee.deployments[this.day.formattedDateIso] ?? [];
            }

            return this.company?.deploymentsWithoutEmployee[this.day.formattedDateIso] ?? [];
        }

        public get deploymentProposals(): DeploymentProposal[] {
            if (this.resource) {
                return this.resource.deploymentProposals[this.day.formattedDateIso] || [];
            }

            return [];
        }

        public get hasLeaveProblems(): boolean {
            return this.leavePeriods.some((leavePeriod: PlanbordLeavePeriod | LeavePeriod): boolean => {
                return 'problematicDays' in leavePeriod
                    && !!leavePeriod.problematicDays
                    && leavePeriod.problematicDays.some((day: Date): boolean => day.valueOf() === this.day.date.valueOf());
            });
        }

        public get stampShifts(): PersonalStampShift[] {
            if (!this.stamp || !this.stamp.personalStampShifts) {
                return [];
            }

            return this.stamp.personalStampShifts.filter((shift: PersonalStampShift): boolean => {
                return shift.dayIndex === this.stampDayIndex;
            }).map((shift: PersonalStampShift): PersonalStampShift => {
                return shift;
            });
        }

        public get stampDayIndex(): number {
            if (!this.stamp) {
                return 0;
            }

            return this.stampOffset % this.stamp.iterationLength;
        }

        public get shouldShowMarkerWarnings(): boolean {
            return !!this.contractDeviations || this.hasLeavePeriodsOrForm;
        }

        public get hasStamp(): boolean {
            return this.stamp !== undefined;
        }

        public get isStampStart(): boolean {
            if (!this.stamp) {
                return false;
            }

            return this.day.date.valueOf() === this.stamp.beginDate.valueOf();
        }

        public get isStampEnd(): boolean {
            if (!this.stamp || !this.stamp.endDate) {
                return false;
            }

            return this.day.date.valueOf() === this.stamp.endDate.valueOf();
        }

        public get isIterationStart(): boolean {
            if (!this.stamp) {
                return false;
            }

            return this.stampOffset % this.stamp.iterationLength === 0;
        }

        public get isIterationEnd(): boolean {
            if (!this.stamp) {
                return false;
            }

            return (this.stampOffset + 1) % this.stamp.iterationLength === 0;
        }

        public get isStampPeriodStart(): boolean {
            return this.resource !== undefined && this.resource.stampDeployedUntil !== null && Dates.Utils.addDays(this.resource.stampDeployedUntil, 1).valueOf() === this.day.date.valueOf();
        }

        public get stampOffset(): number {
            if (!this.stamp) {
                return 0;
            }

            return Dates.Utils.diffInDays(this.day.date, this.stamp.beginDate);
        }

        public get isPublishedEndDate(): boolean {
            return this.roster.publishedUntil !== undefined && (Dates.Utils.diffInDays(this.roster.publishedUntil, this.day.date) === -1);
        }

        public get isProposed(): boolean {
            return this.roster.proposedUntil !== undefined && this.roster.proposedUntil >= this.day.date;
        }

        public get isProposedEndDate(): boolean {
            return this.roster.proposedUntil !== undefined && (Dates.Utils.diffInDays(this.roster.proposedUntil, this.day.date) === -1);
        }

        public get wishInputEndDate(): boolean {
            return this.roster.wishInputClosedUntil !== null && (Dates.Utils.diffInDays(this.roster.wishInputClosedUntil, this.day.date) === -1);
        }

        public get isEmployed(): boolean {
            return CellCapacity.mayContainDeployments(this.day.date, this.resource, this.company, this.companyEmployee);
        }

        public get hasLeavePeriodsOrForm(): boolean {
            return this.leavePeriods.length > 0 || this.hasLeaveForm;
        }

        public get contractDeviations(): (keyof ContractCaptions)[] | undefined {
            if (!this.contractDeviationLabels || !this.resource || !this.resource.contractedTimes) {
                return undefined;
            }

            const keys: Array<keyof ContractCaptions> = [];

            for (const contractedTime of this.resource.contractedTimes) {
                if (contractedTime.beginDate <= this.day.date && contractedTime.endDate >= this.day.date) {
                    if (contractedTime.parentalLeaveHoursPerWeek > 0 && this.contractDeviationLabels.parentalLeave) {
                        keys.push('parentalLeave');
                    }
                    if (contractedTime.otherHoursPerWeek > 0 && this.contractDeviationLabels.other) {
                        keys.push('other');
                    }
                    if (contractedTime.maternityLeaveHoursPerWeek > 0 && this.contractDeviationLabels.pregnant) {
                        keys.push('pregnant');
                    }
                    if (contractedTime.sickHoursPerWeek > 0 && this.contractDeviationLabels.sick) {
                        keys.push('sick');
                    }
                    break;
                }
            }

            return keys.length > 0 ? keys : undefined;
        }

        public get weekendIdentifier(): string | null {
            return this.isInStampPeriod ? this.stampWeekendIdentifier : this.nonStampWeekendIdentifier;
        }

        public get stampWeekendIdentifier(): string | null {
            if (!this.stamp) {
                return null;
            }

            for (const assignedDay of this.stamp.personalStampAssignedDays!) {
                if (assignedDay.dayIndex === this.stampDayIndex) {
                    return this.getAssignedDayTypeName(assignedDay.type);
                }
            }

            return null;
        }

        public get nonStampWeekendIdentifier(): string | null {
            const curDate: string = this.day.formattedDateIso;
            if (this.resource?.assignedDays && curDate in this.resource.assignedDays) {
                const currentType: AssignedDay | undefined = this.resource.assignedDays[curDate]?.find((day: AssignedDay): boolean => {
                    return day.type === RosterDayType.Sunday || day.type === RosterDayType.Saturday;
                });

                if (currentType) {
                    return this.getAssignedDayTypeName(currentType.type);
                }
            }

            return null;
        }

        public getAssignedDayTypeName(type: RosterDayType): string | null {
            if (type === RosterDayType.Sunday) {
                return 'zo';
            }

            if (type === RosterDayType.Saturday) {
                return 'za';
            }

            return null;
        }

        public get usedShiftDeviationStates(): DeploymentState[] | undefined {
            const deploymentStates: DeploymentState[] = [];
            for (const deployment of this.deployments) {
                if ('id' in deployment && deployment.stateId !== DeploymentStateNumber.Normal) {
                    const deploymentState: OrganisationDeploymentState | null = this.organisationDeploymentStates[deployment.stateId] ?? null;
                    if (deploymentState !== null) {
                        deploymentStates.push(deploymentState.id);
                    }
                }
            }

            return deploymentStates.length > 0 ? deploymentStates : undefined;
        }

        public get isWeekend(): boolean {
            if (this.isInStampPeriod && this.stamp) {
                for (const assignedDay of this.stamp.personalStampAssignedDays!) {
                    if (assignedDay.dayIndex === this.stampDayIndex) {
                        return assignedDay.type === RosterDayType.Sunday || assignedDay.type === RosterDayType.Saturday;
                    }
                }
            }

            if (!this.isInStampPeriod) {
                const curDate: string = this.day.formattedDateIso;
                if (this.resource?.assignedDays && curDate in this.resource.assignedDays) {
                    return !!this.resource.assignedDays[curDate]?.some((day: AssignedDay): boolean => {
                        return day.type === RosterDayType.Sunday || day.type === RosterDayType.Saturday;
                    });
                }
            }

            return this.day.weekend;
        }

        public get sickPeriods(): SickPeriod[] {
            if (!this.resource || !this.resource.sickPeriods) {
                return [];
            }

            return this.resource.sickPeriods.filter((period: SickPeriod): boolean => this.day.date >= period.beginDate && (period.endDate === undefined || this.day.date < period.endDate));
        }

        public get sicknessInformation(): SicknessInformation {
            if (this.sickPeriods.length === 0) {
                return {
                    isSick: false,
                    isFullySick: false,
                    hasExpectedRecovery: false,
                };
            }

            const now: Date = new Date();
            let totalSicknessPercentage: number = 0;
            let allSicknessCasesHaveExpectedRecovery: boolean = true;
            for (const sickPeriod of this.sickPeriods) {
                for (let i: number = 0; i < sickPeriod.percentages!.length; i++) {
                    if (this.day.date >= sickPeriod.percentages![i].beginDate && ((i + 1) >= sickPeriod.percentages!.length || this.day.date < sickPeriod.percentages![i + 1].beginDate)) {
                        totalSicknessPercentage += sickPeriod.percentages![i].percentage;
                    }
                }

                if (allSicknessCasesHaveExpectedRecovery
                    && (!!sickPeriod.endDate
                        || !sickPeriod.expectedRecoveryDate
                        || sickPeriod.expectedRecoveryDate <= now
                        || sickPeriod.expectedRecoveryDate > this.day.date
                    )
                ) {
                    allSicknessCasesHaveExpectedRecovery = false;
                }
            }

            return {
                isSick: totalSicknessPercentage > 0,
                isFullySick: totalSicknessPercentage > 99.999999999,
                hasExpectedRecovery: allSicknessCasesHaveExpectedRecovery,
            };
        }

        public get unavailabilities(): Unavailability[] {
            if (!this.resource || !this.resource.unavailabilities) {
                return [];
            }

            return this.resource.unavailabilities[this.day.formattedDateIso] ?? [];
        }

        public get unavailabilityStatus(): UnavailabilityStatus | undefined {
            const todayUnavailabilities: Unavailability[] = this.unavailabilities;
            if (todayUnavailabilities.length) {
                let isAllDayUnavailable: boolean = false;
                for (const unavailability of todayUnavailabilities) {
                    if (unavailability.startTime === AvailabilityUtil.START_OF_DAY_TIME && unavailability.endTime === AvailabilityUtil.END_OF_DAY_TIME) {
                        isAllDayUnavailable = true;
                    }
                }

                return {
                    hasUnavailabilities: true,
                    isEntireDay: isAllDayUnavailable,
                };
            }

            return undefined;
        }

        public get hasWish(): boolean {
            return !!this.wish;
        }

        public get hasComment(): boolean {
            return !!(this.resource && this.resource.employment
                && this.resource.employment.commentsByDate
                && this.resource.employment.commentsByDate[this.day.formattedForComputers]
                && this.resource.employment.commentsByDate[this.day.formattedForComputers].content
                && this.resource.employment.commentsByDate[this.day.formattedForComputers].content.length > 0);
        }

        public openCommentEditor(): void {
            if (this.resource && this.resource.employment) {
                /** @see EmploymentCommentModal.openModal() */
                // @ts-expect-error TS2339
                EmploymentCommentModal.__vfdConstructor.openModal(this.resource.employment, this.day.date);
            }
        }
    }
</script>

<style lang="scss" scoped>
    div.deviation-labels {
        display: flex;
        flex-wrap: wrap;

        .deviation-marker {
            font-weight: bold;
            background: #4d525677;
            color: #fff;
            border-radius: var(--theme-border-radius);
            align-items: center;
            align-content: center;
            display: inline-flex;
            padding: 0 0.4rem;
            margin: 0.1rem;
            word-break: keep-all;
        }
    }

    div.weekend-identifier {
        float: right;
        color: var(--weekend-identifier-color);
        font-style: italic;
        font-size: .8rem;
        line-height: 1.5rem;
        padding-right: .25rem;
    }

    .comment-content {
        display: var(--compact-hide-block);
        border-radius: 10px;
        color: var(--comment-color);
        background: var(--comment-background);
        hyphens: auto;
        overflow-wrap: break-word;
        padding: 0.2rem 0.4rem;
        margin: 0.1rem;
        margin-bottom: 0.25rem;
        cursor: pointer;
    }
</style>
